"use client";
import { FC, useEffect, useRef, useState } from "react";
import AppContainer from "../appContainer";
import { cn } from "@/lib";
import { FlexBetween } from "../ui/flexBox";
import { statsStaticData } from "@/constants";
import { useTranslations } from "next-intl";

// Custom hook for counter animation
const useCounterAnimation = (
  targetValue: number,
  isVisible: boolean,
  duration: number = 2000,
) => {
  const [currentValue, setCurrentValue] = useState(0);

  useEffect(() => {
    if (!isVisible) return;

    const startTime = Date.now();
    const startValue = 0;
    const endValue = targetValue;

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Smoother easing function without the dramatic slowdown at the end
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const current = Math.floor(
        startValue + (endValue - startValue) * easeOutQuart,
      );

      setCurrentValue(current);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Ensure we end with the exact target value
        setCurrentValue(endValue);
      }
    };

    animate();
  }, [isVisible, targetValue, duration]);

  return currentValue;
};

// Counter component
const AnimatedCounter: FC<{ value: string; isVisible: boolean }> = ({
  value,
  isVisible,
}) => {
  const numericValue = parseInt(value, 10);
  const animatedValue = useCounterAnimation(numericValue, isVisible);

  return <span>{animatedValue}</span>;
};

const StatsSection: FC = () => {
  const t = useTranslations();
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Once visible, we can disconnect the observer to prevent re-triggering
          observer.disconnect();
        }
      },
      {
        threshold: 0.3, // Trigger when 30% of the section is visible
        rootMargin: "0px 0px -100px 0px", // Start animation slightly before fully visible
      },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="bg-primary-main mb-40 w-full overflow-hidden bg-[url(/wavy-pattern.svg)] bg-cover py-10 text-white lg:mb-60"
    >
      <AppContainer className={cn("h-auto px-10")}>
        <FlexBetween
          gap={10}
          className="mx-auto flex-wrap md:max-w-[700px] md:gap-0 lg:max-w-[900px] xl:max-w-full"
        >
          {statsStaticData.map((stat, idx) => (
            <FlexBetween gap={2} key={idx} className="lg:gap-4">
              <FlexBetween gap={1} className="font-extrabold md:gap-2">
                <span className="text-secondary-main text-xl md:text-3xl xl:text-5xl">
                  +
                </span>
                <span className="text-4xl md:text-5xl xl:text-6xl">
                  <AnimatedCounter value={stat.value} isVisible={isVisible} />
                </span>
              </FlexBetween>
              <div>
                <div className="text-secondary-main text-sm font-bold lg:text-base xl:text-xl">
                  {t(stat.label as any)}
                </div>
                <div className="text-xs text-white/80 lg:text-sm xl:text-xl">
                  {t(stat.description as any)}
                </div>
              </div>
            </FlexBetween>
          ))}
        </FlexBetween>
      </AppContainer>
    </section>
  );
};

export default StatsSection;
